import { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { useInterval } from '@frontend/timer';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { HOURS_LANE_WIDTH, PROVIDER_COLUMN_GAP_NEW } from './constants';
import { useCalendarView } from './providers';
import {
  ADJUSTED_ONE_HOUR_IN_MINUTES,
  ADJUSTED_ONE_MINUTE_IN_MILLISECONDS,
  calculatePercentage,
  zIndex,
} from './utils';

const getCurrentTimeLabel = (timezone = '', showTimeLabel = false) => {
  if (!showTimeLabel) return null;
  return timezone ? dayjs().tz(timezone).format('HH:mm') : dayjs().format('HH:mm');
};

function calculateTimeProgress(totalDurationInHours: number, timezone?: string) {
  const now = timezone ? dayjs().tz(timezone) : dayjs();
  const minutesPassedSinceOffset = now.hour() * 60 + now.minute();
  const progressPercentage = calculatePercentage(minutesPassedSinceOffset, totalDurationInHours * 60);

  return progressPercentage;
}

type ProgressLineVariant = 'default' | 'enhanced';

type ProgressLineProps = {
  id?: string;
  percentage: number;
  hide?: boolean;
  color: string;
  isTodayProgressLine?: boolean;
  progressTimeLabel?: string | null;
  variant?: ProgressLineVariant;
};

const ProgressLine = ({
  id,
  percentage,
  color,
  hide = false,
  isTodayProgressLine = false,
  progressTimeLabel,
  variant = 'default',
}: ProgressLineProps) => {
  const ref = useRef<HTMLDivElement>(null);

  if (hide) return null;

  if (progressTimeLabel) {
    return (
      <div id={id} ref={ref} css={[styles.container, { top: `${percentage}%`, zIndex: zIndex.progressLineWithLabel }]}>
        <div
          css={[
            styles.line(variant),
            { background: color, display: 'flex', alignItems: 'center', paddingRight: PROVIDER_COLUMN_GAP_NEW },
          ]}
        >
          <div
            css={{
              position: 'sticky',
              left: HOURS_LANE_WIDTH,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: color,
              padding: theme.spacing(0.25, 0.5),
              borderRadius: theme.borderRadius.small,
              width: PROVIDER_COLUMN_GAP_NEW,
              margin: `-${PROVIDER_COLUMN_GAP_NEW}px`,
            }}
          >
            <Text size='small' weight='bold' color='white'>
              {progressTimeLabel}
            </Text>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      id={id}
      ref={ref}
      css={[
        styles.container,
        { top: `${percentage}%` },
        isTodayProgressLine && { width: variant === 'enhanced' ? '100%' : `calc(100% + ${theme.spacing(1)})` },
        { zIndex: variant === 'enhanced' ? zIndex.progressLineWithLabel : zIndex.progressLine },
      ]}
    >
      <div css={[styles.line(variant), { background: color }, isTodayProgressLine && { left: theme.spacing(-1) }]}>
        <div css={[styles.startingDot, { background: color, left: HOURS_LANE_WIDTH }]} />
      </div>
    </div>
  );
};

// Use to show the current time progress line
export const TimeProgress = ({
  numberOfHours,
  officeTimezone,
  showTimeLabel = false,
  variant = 'default',
}: {
  numberOfHours: number;
  officeTimezone?: string;
  showTimeLabel?: boolean;
  variant?: ProgressLineVariant;
}) => {
  const [timeProgressPercentage, setTimeProgressPercentage] = useState(() =>
    calculateTimeProgress(numberOfHours, officeTimezone)
  );
  const [progressTimeLabel, setProgressTimeLabel] = useState(() => getCurrentTimeLabel(officeTimezone, showTimeLabel));

  useInterval(() => {
    const progressPercentage = calculateTimeProgress(numberOfHours, officeTimezone);
    setTimeProgressPercentage(progressPercentage);
    setProgressTimeLabel(getCurrentTimeLabel(officeTimezone, showTimeLabel));
  }, ADJUSTED_ONE_MINUTE_IN_MILLISECONDS);

  // to show the time label on first location section when calendar is scrolled to right
  useEffect(() => {
    setProgressTimeLabel(getCurrentTimeLabel(officeTimezone, showTimeLabel));
  }, [officeTimezone, showTimeLabel]);

  return (
    <ProgressLine
      id='current-time-progress-line'
      percentage={timeProgressPercentage}
      color={theme.colors.primary50}
      progressTimeLabel={progressTimeLabel}
      isTodayProgressLine
      variant={variant}
    />
  );
};

// Use to show the hovered time progress line
export const HoveredTimeProgressLine = ({ numberOfHours }: { numberOfHours: number }) => {
  const { hoveredTimeProgressPosition, backgroundView } = useCalendarView([
    'hoveredTimeProgressPosition',
    'backgroundView',
  ]);
  const progressLinePosition = calculatePercentage(
    hoveredTimeProgressPosition,
    numberOfHours * ADJUSTED_ONE_HOUR_IN_MINUTES
  );

  return (
    <ProgressLine
      hide={backgroundView || hoveredTimeProgressPosition >= 24 * ADJUSTED_ONE_HOUR_IN_MINUTES}
      percentage={progressLinePosition}
      color={theme.colors.critical40}
    />
  );
};

const styles = {
  container: css({
    position: 'absolute',
    zIndex: zIndex.progressLine,
    height: '1.25px',
    width: '100%',
    left: 0,
    scrollMargin: '100px',
  }),
  line: (variant: ProgressLineVariant) =>
    css({
      position: 'absolute',
      left: 0,
      right: 0,
      top: '50%',
      height: variant === 'enhanced' ? '1.5px' : '1px',
      transform: 'translateY(-50%)',
    }),
  startingDot: css({
    position: 'sticky',
    width: '11px',
    height: '11px',
    borderRadius: theme.borderRadius.full,
    top: '50%',
    transform: 'translateY(-50%)',
  }),
};
